import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IonButtons, IonHeader, IonToolbar } from '@ionic/react';
import { ChevronLeft } from './icons';

function pageRootFrom(el: Element | null): HTMLElement | null {
  if (!el) return null;
  return el.closest('ion-modal.show-modal, ion-modal, .ion-page, ion-page') as HTMLElement | null;
}

function findActiveHeader(): HTMLElement | null {
  return document.querySelector(
    'ion-modal.show-modal ion-header.load-detail-header, .ion-page:not(.ion-page-hidden) > ion-header.load-detail-header, ion-page:not(.ion-page-hidden) > ion-header.load-detail-header'
  ) as HTMLElement | null;
}

function measureOnSheet(root: HTMLElement | null): boolean {
  if (!root) return false;
  const header = root.querySelector('ion-header.load-detail-header') as HTMLElement | null;
  const sheet = root.querySelector('.home-sheet') as HTMLElement | null;
  if (!header || !sheet) return false;
  return sheet.getBoundingClientRect().top <= header.getBoundingClientRect().bottom + 4;
}

/** Scroll handler that darkens the transparent header title over the white sheet. */
export function useTransparentHeaderContrast() {
  const [onSheet, setOnSheet] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);

  const onIonScroll = useCallback((ev?: CustomEvent) => {
    const target = (ev?.target as HTMLElement | undefined) || null;
    const root = pageRootFrom(target) || rootRef.current || pageRootFrom(findActiveHeader());
    if (root) rootRef.current = root;
    setOnSheet(measureOnSheet(root));
  }, []);

  useEffect(() => {
    let scrollEl: HTMLElement | null = null;
    let observer: IntersectionObserver | null = null;
    let cancelled = false;
    let raf = 0;

    const runSync = () => {
      if (cancelled) return;
      const root = rootRef.current || pageRootFrom(findActiveHeader());
      if (root) rootRef.current = root;
      setOnSheet(measureOnSheet(root));
    };

    const attach = async () => {
      const header = findActiveHeader();
      const root = pageRootFrom(header);
      if (!root || cancelled) return;
      rootRef.current = root;

      const sheet = root.querySelector('.home-sheet') as HTMLElement | null;
      const content = root.querySelector('ion-content') as HTMLIonContentElement | null;

      if (sheet) {
        observer = new IntersectionObserver(() => runSync(), {
          root: null,
          threshold: [0, 0.05, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 0.95, 1],
        });
        observer.observe(sheet);
      }

      if (content?.getScrollElement) {
        try {
          scrollEl = await content.getScrollElement();
          if (!cancelled && scrollEl) {
            scrollEl.addEventListener('scroll', runSync, { passive: true });
          }
        } catch {
          /* ion-content not ready */
        }
      }

      runSync();
      raf = window.requestAnimationFrame(runSync);
    };

    const id = window.setTimeout(attach, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
      if (raf) window.cancelAnimationFrame(raf);
      observer?.disconnect();
      scrollEl?.removeEventListener('scroll', runSync);
    };
  }, []);

  return { onSheet, onIonScroll };
}

type LoadDetailHeaderProps = {
  title: string;
  onBack: () => void;
  onSheet?: boolean;
  actions?: React.ReactNode;
  className?: string;
};

const LoadDetailHeader: React.FC<LoadDetailHeaderProps> = ({ title, onBack, onSheet = false, actions, className }) => (
  <IonHeader
    className={`load-detail-header${onSheet ? ' load-detail-header--on-sheet' : ''}${className ? ` ${className}` : ''}`}
  >
    <IonToolbar className="load-detail-toolbar">
      <IonButtons slot="start">
        <button type="button" className="load-detail-back" onClick={onBack} aria-label="Go back">
          <ChevronLeft color="#000000" size={24} />
        </button>
      </IonButtons>
      <IonButtons slot="end">
        <span className="load-detail-title">{title}</span>
        {actions && <span className="load-detail-actions">{actions}</span>}
      </IonButtons>
    </IonToolbar>
  </IonHeader>
);

export default LoadDetailHeader;
